import { useCrudService } from 'framework/crud';
import type { ApiRequestOptions } from 'framework/api';
import { type LoadingParams } from 'framework/list';
import { usePeopleManagementApi } from 'infrastructure/api';
import { POSITION_PROFILE_STATUS_ACTIONS } from './constants';
import { positionProfileValidation } from './createupdate/positionProfileValidation';
import type {
  PositionProfile,
  PositionProfileDto,
  PositionProfileFormValues,
  PositionProfileListResponse,
  PositionProfileStatusAction,
} from './types';
import usePositionProfileFinance from './shared/finance/usePositionProfileFinance';
import { mapPositionProfile, mapPositionProfilePayload } from './shared/utils/positionProfileMappers';

const RESOURCE_URL = '/position-profiles';

const usePositionProfilesService = () => {
  const { sendPostRequest } = usePeopleManagementApi();
  const { enrichSalaryRanges, buildSalaryRangePayload } = usePositionProfileFinance();
  const {
    search: searchRequest,
    create: createRequest,
    update: updateRequest,
    getById: getByIdRequest,
  } = useCrudService<PositionProfileDto, PositionProfileListResponse>({
    listResourceUrl: RESOURCE_URL,
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    useService: usePeopleManagementApi,
  });

  const search = async (
    { pageNumber = 0, pageSize = 30, filter = {}, sort }: LoadingParams,
    options?: ApiRequestOptions,
  ) => {
    return searchRequest(
      {
        pageNumber,
        pageSize,
        filter,
        sort,
      },
      options,
    );
  };

  const create = async (values: PositionProfileFormValues): Promise<PositionProfile> =>
    mapPositionProfile(
      await createRequest(await mapPositionProfilePayload(values, buildSalaryRangePayload)),
      enrichSalaryRanges,
    );

  const update = async (id: string, values: PositionProfileFormValues): Promise<PositionProfile> =>
    mapPositionProfile(
      await updateRequest(id, await mapPositionProfilePayload(values, buildSalaryRangePayload)),
      enrichSalaryRanges,
    );

  const getNameById = async (id: string): Promise<string> => (await getByIdRequest(id, true)).name;

  const getById = async (id: string): Promise<PositionProfile> =>
    mapPositionProfile(await getByIdRequest(id, true), enrichSalaryRanges);

  const changeStatus = async (id: string, action: PositionProfileStatusAction) => {
    const response = await sendPostRequest(`${RESOURCE_URL}/${id}/${action}`);
    return mapPositionProfile((await response.json()) as PositionProfileDto, enrichSalaryRanges);
  };

  return {
    search,
    create,
    update,
    getById,
    getNameById,
    deprecate: (id: string) => changeStatus(id, POSITION_PROFILE_STATUS_ACTIONS.DEPRECATE),
    reactivate: (id: string) => changeStatus(id, POSITION_PROFILE_STATUS_ACTIONS.REACTIVATE),
    retire: (id: string) => changeStatus(id, POSITION_PROFILE_STATUS_ACTIONS.RETIRE),
    getValidationSchema: () => Promise.resolve(positionProfileValidation),
    getUpdateValidationSchema: () => Promise.resolve(positionProfileValidation),
  };
};

export default usePositionProfilesService;
